import {merge} from 'lodash'
import {
    delonghiSemantics,
    kenwoodSemantics,
    nutribulletSemantics,
    braunSemantics,
} from './import/semantics'
import {
    Borders,
    Colors,
    IconsSize,
    Radius,
    Shadows,
    Spacing,
    Sizes,
    Semantics,
    SemanticsObject,
    FontWeights,
} from './types/semantics.types'

const semanticsFromBrand = (brand: string): Semantics => {
    let {
        colors,
        borders,
        radius,
        shadows,
        spacing,
        iconsSize,
        sizes,
        fontWeights,
        semanticOverrides,
    }: Semantics = {
        colors: {} as Colors,
        borders: {} as Borders,
        radius: {} as Radius,
        shadows: {} as Shadows,
        spacing: {} as Spacing,
        iconsSize: {} as IconsSize,
        sizes: {} as Sizes,
        fontWeights: {} as FontWeights,
        semanticOverrides: {},
    }

    switch (brand) {
        case 'kenwood':
            colors = kenwoodSemantics.colors
            borders = kenwoodSemantics.borders
            radius = kenwoodSemantics.radius
            iconsSize = kenwoodSemantics.iconsSize
            spacing = kenwoodSemantics.spacing
            shadows = kenwoodSemantics.shadows
            fontWeights = kenwoodSemantics.fontWeights
            semanticOverrides = kenwoodSemantics.semanticOverrides
            break
        case 'nutribullet':
            colors = nutribulletSemantics.colors
            borders = nutribulletSemantics.borders
            radius = nutribulletSemantics.radius
            iconsSize = nutribulletSemantics.iconsSize
            shadows = nutribulletSemantics.shadows
            spacing = nutribulletSemantics.spacing
            fontWeights = nutribulletSemantics.fontWeights
            semanticOverrides = nutribulletSemantics.semanticOverrides
            break
        case 'braun':
            colors = braunSemantics.colors
            borders = braunSemantics.borders
            radius = braunSemantics.radius
            iconsSize = braunSemantics.iconsSize
            spacing = braunSemantics.spacing
            shadows = braunSemantics.shadows
            fontWeights = braunSemantics.fontWeights
            semanticOverrides = braunSemantics.semanticOverrides
            break
        case 'delonghi':
        default:
            colors = delonghiSemantics.colors
            borders = delonghiSemantics.borders
            radius = delonghiSemantics.radius
            iconsSize = delonghiSemantics.iconsSize
            sizes = delonghiSemantics.sizes
            shadows = delonghiSemantics.shadows
            spacing = delonghiSemantics.spacing
            fontWeights = delonghiSemantics.fontWeights
            semanticOverrides = {}
            break
    }
    return {
        colors,
        borders,
        radius,
        spacing,
        iconsSize,
        sizes,
        shadows,
        fontWeights,
        semanticOverrides,
    }
}

const getSemantics = (brand: string): SemanticsObject => {
    const {
        colors,
        borders,
        radius,
        shadows,
        spacing,
        iconsSize,
        sizes,
        semanticOverrides,
        fontWeights,
    } = semanticsFromBrand(brand)

    const standardSemantics = {
        colors: {
            text: {
                //old semantic tokens
                default: colors.black[100],
                primary: colors['brand-secondary'][100],
                secondary: colors.black[90],
                tertiary: colors['brand-primary'][100],
                inverse: colors.white,
                disable: colors.black[50],
                success: colors.success[100],
                danger: colors.danger[100],
                violet: colors['brand-tertiary'][100],
                warning: colors.warning[100],
                'light-warning': colors.warning[80],
                'date-picker-grey': colors['date-picker-grey'],
                grey: colors.black[90],

                //new semantic tokens
                neutral: {
                    dark: colors.neutral?.[950],
                },
                accent: {
                    default: colors.brand?.[400],
                },
            },
            border: {
                //old semantic tokens
                light: colors.borderLight,
                dark: colors.black[100],
                danger: colors.danger[100],
                table: colors.black[20],
                secondary: colors.black[60],
                disabled: colors.black[30],
                success: colors.success[100],
                'pdp-banner-mobile': colors['brand-secondary'][80],

                //new semantic tokens
                neutral: {
                    default: colors.neutral?.[400],
                    subtle: colors.neutral?.[300],
                    strong: colors.neutral?.[500],
                    highlight: colors.white,
                    inverse: 'border-inverse',
                    footer: colors.neutral?.alphaWhite?.[100],
                    overlay: colors.neutral?.alphaWhite?.[400],
                    focus: colors.brand?.[800],
                },

                accent: {
                    default: colors.brand?.[950],
                    light: colors.brand?.[800],
                },

                positive: {
                    default: colors.green?.[950],
                },

                negative: {
                    default: colors.red?.[950],
                },
            },
            background: {
                //old semantic tokens
                default: colors.white,
                dark: colors.black[100],
                'light-dark': colors.black[10],
                brand: colors['brand-secondary'][100],
                alternative: colors['brand-primary'][100],
                light: colors.black[10],
                grey: colors.black[20],
                'light-grey': colors.black[20],
                'light-blue': colors['brand-tertiary'][100],
                info: colors['brand-tertiary'][60],
                danger: colors.danger[60],
                success: colors.success[60],
                'overlay-modal': colors.overlayBackgroundModal,
                image: colors.imageBackground,
                'pdp-banner-tag': colors['brand-tertiary'][80],
                promo: colors['promo-background'],

                //new semantic tokens
                base: {
                    default: colors.neutral?.[50],
                    elevated: colors.neutral?.[0],
                    under: colors.neutral?.[100],
                    deep: colors.neutral?.[300],
                    inverse: colors.neutral?.[950],
                    overlay: colors.neutral?.alphaDark?.[500],
                    'check-icon-modal': colors.blue?.[700],
                    over: colors.neutral?.[25],
                },

                neutral: {
                    default: colors.neutral?.[50],
                    hover: colors.neutral?.[0],
                    muted: colors.neutral?.[300],
                    dark: colors.neutral?.alphaDark?.[950],
                    bronze: colors.bronze?.[900],
                    bronzeAlpha: colors.bronze?.alpha?.[900],
                    blue: colors?.blue?.alpha?.[950],
                    white: colors?.neutral?.alphaWhite?.[400],
                },

                strong: {
                    default: colors.neutral?.[950],
                    hover: colors.neutral?.alphaDark?.[900],
                    muted: colors.neutral?.alphaDark?.[100],
                    footer: colors.neutral?.alphaWhite?.[900],
                },

                accent: {
                    default: colors.brand?.[950],
                    hover: colors.brand?.[900],
                    muted: colors.brand?.[700],
                },

                positive: {
                    default: colors.green?.[900],
                    hover: colors.green?.[800],
                    muted: colors.green?.[50],
                },

                energy: {
                    a: colors.green?.[900] || '#009640',
                },

                negative: {
                    default: colors.red?.[900],
                    hover: colors.red?.[800],
                    muted: colors.red?.[50],
                },
            },
            //new semantic token
            foreground: {
                neutral: {
                    default: colors.neutral?.[950],
                    muted: colors.neutral?.[900],
                    // subtle: colors.neutral?.[800],
                    subtle: 'oklch(48.72% 0.0161 276.78)',
                    dim: colors.neutral?.[600],
                    inverse: colors.neutral?.[50],
                },

                accent: {
                    default: colors.brand?.[950],
                    inverse: colors.brand?.[50],
                },

                positive: {
                    default: colors.green?.[950],
                    inverse: colors.green?.[50],
                },

                negative: {
                    default: colors.red?.[950],
                    inverse: colors.red?.[50],
                },
            },
            button: {
                text: {
                    'primary-default': colors.white,
                    'primary-hover': colors.white,
                    'primary-focused': colors.white,
                    'primary-disabled': colors.black[50],
                    'primary-pressed': colors.white,
                    'secondary-default': colors['brand-secondary'][100],
                    'secondary-hover': colors.white,
                    'secondary-focused': colors.white,
                    'secondary-disabled': colors.black[50],
                    'secondary-pressed': colors.success[100],
                    'icon-default': colors['brand-secondary'][100],
                    'icon-hover': colors.black[50],
                    'icon-disabled': colors.black[30],
                    'icon-pressed': colors.success[100],
                    'text-default': colors['brand-primary'][100],
                    'text-hover': colors['brand-primary'][80], //same as primary-hover background
                    // inverse
                    'inverse-primary-default': colors.black[100],
                    'inverse-primary-hover': colors.black[100],
                    'inverse-primary-focused': colors.black[100],
                    'inverse-primary-disabled': colors.black[60],
                    'inverse-primary-pressed': colors.white,
                    'inverse-secondary-default': colors.white,
                    'inverse-secondary-hover': colors.black[100],
                    'inverse-secondary-focused': colors.black[100],
                    'inverse-secondary-disabled': colors.black[50],
                    'inverse-secondary-pressed': colors.success[100],
                    'inverse-icon-default': colors.white,
                    'inverse-icon-hover': colors.black[30],
                    'inverse-icon-disabled': colors.black[60],
                    'inverse-icon-pressed': colors.success[100],
                    'inverse-text-default': colors.white,
                    'inverse-text-hover': colors.black[30],
                },
                background: {
                    'primary-default': colors['brand-primary'][100],
                    'primary-hover': colors['brand-primary'][80],
                    'primary-focused': colors['brand-primary'][100],
                    'primary-disabled': colors.black[20],
                    'primary-pressed': colors.success[100],
                    'secondary-default': colors.null,
                    'secondary-hover': colors.black[70],
                    'secondary-focused': colors['brand-primary'][100],
                    'secondary-disabled': colors.null,
                    'secondary-pressed': colors.null,
                    'icon-default': colors.null,
                    'icon.hover': colors.null,
                    'icon.disabled': colors.null,
                    'text-default': colors.null,
                    'text-hover': colors.null,
                    // inverse
                    'inverse-primary-default': colors.white,
                    'inverse-primary-hover': colors.black[30],
                    'inverse-primary-focused': colors.white,
                    'inverse-primary-disabled': colors.black[30],
                    'inverse-primary-pressed': colors.success[100],
                    'inverse-secondary-default': colors.null,
                    'inverse-secondary-hover': colors.black[30],
                    'inverse-secondary-focused': colors.white,
                    'inverse-secondary-disabled': colors.null,
                    'inverse-secondary-pressed': colors.null,
                    'inverse-icon-default': colors.null,
                    'inverse-icon-hover': colors.null,
                    'inverse-icon-disabled': colors.null,
                    'inverse-text-default': colors.null,
                    'inverse-text-hover': colors.null,
                    'promo-background': colors['promo-background'],
                },
                border: {
                    'primary-default': colors.null,
                    'primary-hover': colors.null,
                    'primary-focused': colors['brand-secondary'][100],
                    'primary-disabled': colors.null,
                    'secondary-default': colors['brand-secondary'][100],
                    'secondary-hover': colors.null,
                    'secondary-focused': colors['brand-secondary'][100],
                    'secondary-disabled': colors.black[50],
                    'secondary-pressed': colors.success[100],
                    'icon-default': colors.null,
                    'icon-hover': colors.null,
                    'icon-disabled': colors.null,
                    'text-default': colors.null,
                    'text-hover': colors.null,
                    // inverse
                    'inverse-primary-default': colors.null,
                    'inverse-primary-hover': colors.null,
                    'inverse-primary-focused': colors.black[40],
                    'inverse-primary-disabled': colors.null,
                    'inverse-secondary-default': colors.white,
                    'inverse-secondary-hover': colors.null,
                    'inverse-secondary-focused': colors.black[40],
                    'inverse-secondary-disabled': colors.black[60],
                    'inverse-secondary-pressed': colors.white,
                    'inverse-icon-default': colors.null,
                    'inverse-icon-hover': colors.null,
                    'inverse-icon-disabled': colors.null,
                    'inverse-text-default': colors.null,
                    'inverse-text-hover': colors.null,
                    'accessibility-blue-light': colors['accessibility-blue-light'],
                    'accessibility-blue-dark': colors['accessibility-blue-dark'],
                },
            },
            radio: {
                bg: {
                    default: colors.blue?.[700],
                    hover: colors.blue?.[800],
                    checkedDisabled: colors.blue?.alpha?.[500],
                },
            },
            label: {
                text: {
                    primary: colors.white,
                    secondary: colors['brand-secondary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[60],
                },
                background: {
                    primary: colors.brand?.[950],
                    secondary: colors['brand-tertiary'][100],
                    success: colors.success[60],
                    danger: colors.danger[60],
                    disable: colors.black[20],
                },
            },
            icons: {
                text: {
                    primary: colors.white,
                    secondary: colors.black[100],
                    tertiary: colors['brand-primary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[60],
                },
                background: {
                    primary: colors.white,
                    secondary: colors.black[100],
                    tertiary: colors['brand-primary'][100],
                    success: colors.success[100],
                    danger: colors.danger[100],
                    disable: colors.black[20],
                },
            },
            checkbox: {
                bg: {
                    checked: colors.blue?.[700],
                    checkedHover: colors.blue?.[800],
                    checkedDisabled: colors.blue?.alpha?.[500],
                },
            },
        },
        space: {
            micro: spacing.micro,
            xxxsmall: spacing.xxxsmall,
            'xxxsmall-1': spacing['xxxsmall-1'],
            xxsmall: spacing.xxsmall,
            'xxsmall-1': spacing['xxsmall-1'],
            xsmall: spacing.xsmall,
            'xsmall-1': spacing['xsmall-1'],
            small: spacing.small,
            'small-1': spacing['small-1'],
            medium: spacing.medium,
            'medium-1': spacing['medium-1'],
            'medium-2': spacing['medium-2'],
            'medium-3': spacing['medium-3'],
            large: spacing.large,
            xlarge: spacing.xlarge,

            page: spacing.page,
        },
        borders: {
            null: borders.null,
            small: borders.small,
            large: borders.large,
            button: {
                border: {
                    'primary-default': 'none',
                    'primary-hover': 'none',
                    'primary-focused': borders.large,
                    'primary-disabled': 'none',
                    'secondary-default': borders.small,
                    'secondary-hover': 'none',
                    'secondary-focused': borders.large,
                    'secondary-disabled': borders.small,
                    'icon-default': 'none',
                    'icon-hover': 'none',
                    'icon-disabled': 'none',
                    'text-default': 'none',
                    'text-hover': 'none',
                    //inverse
                    'inverse-primary-default': 'none',
                    'inverse-primary-hover': 'none',
                    'inverse-primary-focused': borders.large,
                    'inverse-primary-disabled': 'none',
                    'inverse-secondary-default': borders.small,
                    'inverse-secondary-hover': 'none',
                    'inverse-secondary-focused': borders.large,
                    'inverse-secondary-disabled': borders.small,
                    'inverse-icon-default': 'none',
                    'inverse-icon-hover': 'none',
                    'inverse-icon-disabled': 'none',
                    'inverse-text-default': 'none',
                    'inverse-text-hover': 'none',
                },
            },
        },
        radii: {
            button: {
                radius: {
                    'primary-default': radius.button,
                    'primary-hover': radius.button,
                    'primary-focused': radius.button,
                    'primary-disabled': radius.button,
                    'secondary-default': radius.button,
                    'secondary-hover': radius.button,
                    'secondary-focused': radius.button,
                    'secondary-disabled': radius.button,
                    'icon-default': radius.null,
                    'icon-hover': radius.null,
                    'icon-disabled': radius.null,
                    'text-default': radius.null,
                    'text-hover': radius.null,
                    //inverse
                    'inverse-primary-default': radius.button,
                    'inverse-primary-hover': radius.button,
                    'inverse-primary-focused': radius.button,
                    'inverse-primary-disabled': radius.button,
                    'inverse-secondary-default': radius.button,
                    'inverse-secondary-hover': radius.button,
                    'inverse-secondary-focused': radius.button,
                    'inverse-secondary-disabled': radius.button,
                    'inverse-icon-default': radius.null,
                    'inverse-icon-hover': radius.null,
                    'inverse-icon-disabled': radius.null,
                    'inverse-text-default': radius.null,
                    'inverse-text-hover': radius.null,
                },
            },
            //new tokens
            null: radius.null,
            small: radius.small,
            medium: radius.medium,
            'medium-1': radius['medium-1'],
            large: radius.large,
            'large-1': radius['large-1'],
            xl: radius.xl,
            'xl-1': radius['xl-1'],
            xxl: radius.xxl,
            full: radius.full,
        },
        sizes: {
            iconsSize: {
                small: iconsSize.small,
                medium: iconsSize.medium,
                large: iconsSize.large,
                xlarge: iconsSize.xlarge,
            },
            button: {
                small: sizes?.button?.small,
                base: sizes?.button?.base,
                large: sizes?.button?.large,
                xl: sizes?.button?.xl,
            },
            input: {
                small: sizes?.input?.small,
                base: sizes?.input?.base,
                large: sizes?.input?.large,
                xl: sizes?.input?.xl,
            },
        },
        shadows: {
            borderShadow: shadows.borderShadow,
            borderShadowBottom: shadows.borderShadowBottom,
            neutral: {
                xs: shadows.neutral?.xs,
                sm: shadows.neutral?.sm,
                md: shadows.neutral?.md,
                lg: shadows.neutral?.lg,
                xl: shadows.neutral?.xl,
            },
            accent: {
                xs: shadows.accent?.xs,
                sm: shadows.accent?.sm,
                md: shadows.accent?.md,
                lg: shadows.accent?.lg,
                xl: shadows.accent?.xl,
            },
            positive: {
                xs: shadows.positive?.xs,
                sm: shadows.positive?.sm,
                md: shadows.positive?.md,
                lg: shadows.positive?.lg,
                xl: shadows.positive?.xl,
            },
            negative: {
                xs: shadows.negative?.xs,
                sm: shadows.negative?.sm,
                md: shadows.negative?.md,
                lg: shadows.negative?.lg,
                xl: shadows.negative?.xl,
            },
        },
        fontWeights: {
            light: fontWeights.light,
            normal: fontWeights.normal,
            regular: fontWeights.regular,
            medium: fontWeights.medium,
            semibold: fontWeights.semibold,
            bold: fontWeights.bold,
        },
    }

    return merge({}, standardSemantics, semanticOverrides) as SemanticsObject
}

export default getSemantics
